import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../Entry/Entry.css";

gsap.registerPlugin(ScrollTrigger);

const PARTICLES = Array.from({ length: 60 }, (_, i) => ({
  id: i,
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: Math.random() * 3 + 1,
}));

const GRID_COUNT = 25;

const Entry = () => {
  const containerRef = useRef(null);
  const headingRef = useRef(null);
  const subRef = useRef(null);
  const badgeRef = useRef(null);
  const statsRef = useRef(null);
  const gridRef = useRef(null);
  const imageRef = useRef(null);
  const lineRef = useRef(null);
  const scanlineRef = useRef(null);
  const ctaRef = useRef(null);
  const particlesRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      /* ── Image reveal ── */
      tl.fromTo(
        imageRef.current,
        { scale: 1.18, filter: "brightness(0) blur(12px)" },
        {
          scale: 1,
          filter: "brightness(0.45) blur(0px)",
          duration: 1.8,
          ease: "expo.out",
        },
      );

      /* ── Scanline sweep ── */
      tl.fromTo(
        scanlineRef.current,
        { scaleX: 0, opacity: 1 },
        {
          scaleX: 1,
          opacity: 0,
          duration: 0.9,
          ease: "power2.inOut",
          transformOrigin: "left center",
        },
        "-=1",
      );

      /* ── Grid flash ── */
      const cells = gridRef.current?.querySelectorAll(".entry-gc");
      if (cells) {
        tl.fromTo(
          cells,
          { opacity: 0 },
          {
            opacity: 1,
            duration: 0.04,
            stagger: { amount: 0.6, from: "random" },
            ease: "none",
          },
          "-=1.2",
        );
        tl.to(
          cells,
          { opacity: 0, duration: 0.5, stagger: { amount: 0.4 }, delay: 0.1 },
          "-=0.2",
        );
      }

      /* ── Badge ── */
      tl.fromTo(
        badgeRef.current,
        { y: -30, opacity: 0, scale: 0.85 },
        { y: 0, opacity: 1, scale: 1, duration: 0.7 },
        "-=0.5",
      );

      /* ── Accent line ── */
      tl.fromTo(
        lineRef.current,
        { scaleX: 0 },
        {
          scaleX: 1,
          duration: 0.8,
          ease: "power2.inOut",
          transformOrigin: "left",
        },
        "-=0.4",
      );

      /* ── Heading chars ── */
      const chars = headingRef.current?.querySelectorAll(".entry-char");
      if (chars) {
        tl.fromTo(
          chars,
          { y: 110, opacity: 0, rotationX: -80 },
          {
            y: 0,
            opacity: 1,
            rotationX: 0,
            duration: 1,
            stagger: 0.035,
            ease: "back.out(1.4)",
          },
          "-=0.5",
        );
      }

      /* ── Sub text words ── */
      const words = subRef.current?.querySelectorAll(".entry-word");
      if (words) {
        tl.fromTo(
          words,
          { y: 30, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.6, stagger: 0.06 },
          "-=0.4",
        );
      }

      /* ── Stats ── */
      const stats = statsRef.current?.querySelectorAll(".entry-stat-item");
      if (stats) {
        tl.fromTo(
          stats,
          { y: 40, opacity: 0, scale: 0.9 },
          { y: 0, opacity: 1, scale: 1, duration: 0.6, stagger: 0.12 },
          "-=0.3",
        );
      }

      /* ── CTA ── */
      tl.fromTo(
        ctaRef.current,
        { y: 25, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6 },
        "-=0.4",
      );

      /* ── Particles drift ── */
      particlesRef.current?.querySelectorAll(".entry-pdot").forEach((dot) => {
        gsap.to(dot, {
          y: `random(-30, 30)`,
          x: `random(-20, 20)`,
          opacity: `random(0.1, 0.7)`,
          duration: `random(4, 8)`,
          repeat: -1,
          yoyo: true,
          ease: "sine.inOut",
          delay: `random(0, 3)`,
        });
      });

      /* ── Scroll parallax ── */
      ScrollTrigger.create({
        trigger: containerRef.current,
        start: "top top",
        end: "bottom top",
        scrub: true,
        onUpdate: (self) => {
          gsap.to(imageRef.current, {
            y: self.progress * 120,
            ease: "none",
            duration: 0,
          });
        },
      });
    }, containerRef);

    /* ── Mouse parallax ── */
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { width, height } = containerRef.current.getBoundingClientRect();
      const xPct = (clientX / width - 0.5) * 2;
      const yPct = (clientY / height - 0.5) * 2;
      gsap.to(".entry-parallax-1", {
        x: xPct * -18,
        y: yPct * -12,
        duration: 1.2,
        ease: "power2.out",
      });
      gsap.to(".entry-parallax-2", {
        x: xPct * -8,
        y: yPct * -6,
        duration: 1.4,
        ease: "power2.out",
      });
      gsap.to(imageRef.current, {
        x: xPct * 10,
        duration: 1.8,
        ease: "power2.out",
      });
    };

    const el = containerRef.current;
    el?.addEventListener("mousemove", handleMouseMove);
    return () => {
      el?.removeEventListener("mousemove", handleMouseMove);
      ctx.revert();
    };
  }, []);

  const headingText = "Case Studies";
  const subText =
    "Discover how we've helped clients navigate complex legal battles—from high-stakes litigation to life-changing settlements. Real results. Real impact.";

  return (
    <div ref={containerRef} className="entry-container">
      {/* Background image */}
      <div className="entry-image-wrap">
        <img
          ref={imageRef}
          src="/casestudy.webp"
          alt="Case Study"
          className="entry-image"
        />
        <div className="entry-gradient-overlay" />
        <div className="entry-noise-overlay" />
      </div>

      {/* Scanline sweep */}
      <div ref={scanlineRef} className="entry-scanline" />

      {/* Grid */}
      <div ref={gridRef} className="entry-grid">
        {Array.from({ length: GRID_COUNT }).map((_, i) => (
          <div key={i} className="entry-gc" />
        ))}
      </div>

      {/* Particles */}
      <div ref={particlesRef} className="entry-particles">
        {PARTICLES.map((p) => (
          <div
            key={p.id}
            className="entry-pdot"
            style={{
              left: `${p.x}%`,
              top: `${p.y}%`,
              width: p.size,
              height: p.size,
            }}
          />
        ))}
      </div>

      {/* Corner brackets */}
      <div className="entry-corner entry-corner--tl">
        <span />
        <span />
      </div>
      <div className="entry-corner entry-corner--tr">
        <span />
        <span />
      </div>
      <div className="entry-corner entry-corner--bl">
        <span />
        <span />
      </div>
      <div className="entry-corner entry-corner--br">
        <span />
        <span />
      </div>

      {/* Main content */}
      <div className="entry-content entry-parallax-1">
        <div ref={badgeRef} className="entry-badge">
          <span className="entry-badge-dot" />
          <span className="entry-badge-text">Client Victories</span>
        </div>

        <div ref={lineRef} className="entry-line" />

        <h1 ref={headingRef} className="entry-heading" aria-label={headingText}>
          {headingText.split("").map((char, i) => (
            <span key={i} className="entry-char">
              {char === " " ? "\u00A0" : char}
            </span>
          ))}
        </h1>

        <p ref={subRef} className="entry-sub">
          {subText.split(" ").map((word, i) => (
            <span key={i} className="entry-word">
              {word}
            </span>
          ))}
        </p>

        <div ref={statsRef} className="entry-stats">
          {[
            { num: "500+", label: "Cases Won" },
            { num: "98%", label: "Success Rate" },
            { num: "₹1CR", label: "Total Recovered" },
          ].map((s, i) => (
            <div key={i} className="entry-stat-item">
              <span className="entry-stat-num">{s.num}</span>
              <span className="entry-stat-label">{s.label}</span>
              {i < 2 && <div className="entry-stat-divider" />}
            </div>
          ))}
        </div>

        <div ref={ctaRef} className="entry-cta">
          <button className="entry-btn entry-btn--primary">
            <span>Explore Cases</span>
            <span className="entry-btn-arrow">→</span>
          </button>
          <button className="entry-btn entry-btn--secondary">
            Our Approach
          </button>
        </div>
      </div>

      {/* Accent circle */}
      <div className="entry-accent-circle entry-parallax-2" />

      {/* Scroll hint */}
      <div className="entry-scroll-hint">
        <div className="entry-scroll-line" />
        <span className="entry-scroll-text">Scroll</span>
      </div>
    </div>
  );
};

export default Entry;
